import { useCallback, useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'

import { showSnackbar } from '@b-stock/fe-core/bstock-react'
import type { PayMethod } from '@b-stock/payments-methods-api-client'

import api from '@api/payment'
import FormattedMessage from '@helpers/FormattedMessage'
import useUserInfo from '@helpers/useUserInfo'
import { queryClient } from '@store/data'

import { useStatefulMutation } from '../mutation'

const baseKey = 'paymentMethods'
const creditsKey = `${baseKey}Credits`
const deleteKey = `${baseKey}Delete`
const setAsDefaultKey = `${baseKey}SetAsDefault`

const getTimestamp = (dateStr: string) => new Date(dateStr).valueOf()

const byPrimaryAt = (a: PayMethod, b: PayMethod) =>
  getTimestamp(b.primaryAt) || 0 - getTimestamp(a.primaryAt) || 0

export const usePaymentMethods = (params = {}) => {
  const { accountId: id } = useUserInfo()

  const queryKey = useMemo(() => [baseKey, id, params], [id, params])

  const query = useQuery({
    queryKey: queryKey,

    queryFn: async () => {
      const data = await api.fetchPaymentMethods({
        entityId: id as string,
        params,
      })
      return data.sort(byPrimaryAt)
    },

    enabled: !!id,
    placeholderData: [],
  })

  const creditsQuery = useQuery({
    queryKey: [creditsKey],

    queryFn: async () => {
      const { id: creditId, amount } = await api.fetchBstockCredit({
        accountId: id as string,
      })
      return { creditId, amount }
    },

    enabled: !!id,
  })

  const invalidateList = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey })
  }, [queryKey])

  const deleteMutation = useStatefulMutation(
    async ({ id, methodTitle, nextDefaultPaymentId }: DeleteOptions) => {
      if (nextDefaultPaymentId) {
        await api.setPaymentMethodAsDefault({ id: nextDefaultPaymentId })
      }

      await api.deletePaymentMethod({ id })
      return methodTitle
    },
    {
      mutationKey: [deleteKey],

      onSuccess: async (methodTitle) => {
        await invalidateList()
        showSnackbar({
          content: (
            <FormattedMessage
              id="PaymentMethods.modal.delete.success"
              values={{ methodTitle }}
            />
          ),
        })
      },

      onError: (error, { methodTitle }) => {
        type ApiError = {
          response?: {
            data?: { meta?: { errorCode?: string; message?: string } }
          }
        }
        const meta = (error as ApiError).response?.data?.meta
        // The payments API returns meta.errorCode='unpaid_invoices_exists' when
        // the method is tied to open invoices. In that case, use the API's own
        // message instead of our generic fallback (FP-949).
        const content =
          meta?.errorCode === 'unpaid_invoices_exists' && meta.message ? (
            meta.message
          ) : (
            <FormattedMessage
              id="PaymentMethods.modal.delete.error"
              values={{ methodTitle }}
            />
          )

        showSnackbar({ content })
        return error
      },
    }
  )

  const setAsDefaultMutation = useStatefulMutation(
    async ({ id, methodTitle }: SetDefaultOptions) => {
      await api.setPaymentMethodAsDefault({ id })
      return methodTitle
    },
    {
      mutationKey: [setAsDefaultKey],

      onSuccess: async (methodTitle) => {
        await invalidateList()
        showSnackbar({
          content: (
            <FormattedMessage
              id={
                methodTitle
                  ? 'PaymentMethods.modal.setAsDefault.success'
                  : 'PaymentMethods.modal.setAsDefault.newPrimary'
              }
              values={{ methodTitle }}
            />
          ),
          options: { autoClose: true },
        })
      },

      onError: (error, { methodTitle }) => {
        showSnackbar({
          content: (
            <FormattedMessage
              id="PaymentMethods.modal.setAsDefault.error"
              values={{ methodTitle }}
            />
          ),
        })
        return error
      },
    }
  )

  return {
    ...query,
    methods: query.data,
    credits: creditsQuery.data,
    creditsLoading: creditsQuery.isLoading,

    deletePaymentMethod: deleteMutation.mutateAsync,
    deletePaymentMethodState: deleteMutation.state,

    setPaymentMethodAsDefault: setAsDefaultMutation.mutateAsync,
    setPaymentMethodAsDefaultState: setAsDefaultMutation.state,
  }
}

interface DeleteOptions {
  id: string
  methodTitle: string
  nextDefaultPaymentId: string
}

interface SetDefaultOptions {
  id: string
  methodTitle?: string
}
